const DETAILS_BODY_SELECTOR = '.studio-product-details__body';
const INDEX_SELECTOR = '[data-studio-product-details-index]';
const CHAPTER_HEADING_SELECTOR = '[data-studio-product-chapter-heading]';

const normalizeText = (value) => value.replace(/\s+/g, ' ').trim();

function chapterId(heading, usedIds) {
  if (heading.id) {
    usedIds.add(heading.id);
    return heading.id;
  }

  const slug = normalizeText(heading.textContent)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'chapter';
  const baseId = `product-details-${slug}`;
  let id = baseId;
  let suffix = 2;

  while (usedIds.has(id)) {
    id = `${baseId}-${suffix}`;
    suffix += 1;
  }

  heading.id = id;
  usedIds.add(id);
  return id;
}

function excerpt(panel) {
  const paragraph = Array.from(panel.children).find((node) => node.tagName === 'P');
  if (!paragraph) return '';

  const words = normalizeText(paragraph.textContent).split(' ');
  if (!words[0]) return '';
  return words.length > 18 ? `${words.slice(0, 18).join(' ')}…` : words.join(' ');
}

function enhanceBody(body) {
  if (body.dataset.studioDetailsEnhanced === 'true') return true;

  const nodes = Array.from(body.childNodes);
  const chapterIndexes = nodes
    .map((node, index) => node.nodeType === Node.ELEMENT_NODE && node.tagName === 'H3' ? index : -1)
    .filter((index) => index >= 0);
  if (!chapterIndexes.length || chapterIndexes.some((index) => !normalizeText(nodes[index].textContent))) return false;

  const openingNodes = nodes.slice(0, chapterIndexes[0]);
  const chapterGroups = chapterIndexes.map((startIndex, index) => {
    const endIndex = chapterIndexes[index + 1] || nodes.length;
    return nodes.slice(startIndex, endIndex);
  });
  const assignedNodes = openingNodes.concat(...chapterGroups);
  if (assignedNodes.length !== nodes.length || new Set(assignedNodes).size !== nodes.length) return false;

  const usedIds = new Set(Array.from(document.querySelectorAll('[id]'), (node) => node.id));
  const opening = document.createElement('div');
  opening.className = 'studio-product-details__opening';
  opening.append(...openingNodes);
  const firstMeaningfulNode = openingNodes.find((node) => (
    node.nodeType === Node.ELEMENT_NODE
    || (node.nodeType === Node.TEXT_NODE && normalizeText(node.textContent))
  ));
  if (firstMeaningfulNode instanceof HTMLElement && firstMeaningfulNode.tagName === 'H2') {
    firstMeaningfulNode.classList.add('studio-product-details__product-title');

    const titleIndex = openingNodes.indexOf(firstMeaningfulNode);
    const nodesBeforeTitle = openingNodes.slice(0, titleIndex);
    const lead = document.createElement('div');
    lead.className = 'studio-product-details__lead';
    lead.append(...openingNodes.slice(titleIndex + 1));
    opening.replaceChildren(...nodesBeforeTitle, firstMeaningfulNode, lead);
  }

  const chapterGrid = document.createElement('div');
  chapterGrid.className = 'studio-product-details__chapters';

  chapterGroups.forEach((group, index) => {
    const heading = group[0];
    const chapter = document.createElement('section');
    chapter.className = 'studio-product-details__chapter';
    chapter.dataset.studioProductChapter = String(index + 1);

    const chapterNumber = document.createElement('span');
    chapterNumber.className = 'studio-product-details__chapter-number';
    chapterNumber.setAttribute('aria-hidden', 'true');
    chapterNumber.textContent = String(index + 1).padStart(2, '0');

    heading.classList.add('studio-product-details__chapter-heading');
    heading.dataset.studioProductChapterHeading = '';
    chapterId(heading, usedIds);
    heading.tabIndex = -1;
    chapter.append(chapterNumber, ...group);

    chapterGrid.append(chapter);
  });

  const output = document.createDocumentFragment();
  if (opening.childNodes.length) output.append(opening);
  output.append(chapterGrid);
  body.replaceChildren(output);
  body.dataset.studioDetailsEnhanced = 'true';
  return true;
}

function resetIndex(index) {
  const list = index.querySelector('.studio-product-details-index__list');
  if (list) list.replaceChildren();
  index.hidden = true;
}

function buildIndex(body, index) {
  const headings = Array.from(body.querySelectorAll(CHAPTER_HEADING_SELECTOR));
  if (!headings.length) {
    resetIndex(index);
    return;
  }

  const list = index.querySelector('.studio-product-details-index__list');
  if (!list) return;
  list.replaceChildren();

  headings.forEach((heading, indexPosition) => {
    const item = document.createElement('li');
    const link = document.createElement('a');
    const number = document.createElement('span');
    const copy = document.createElement('span');
    const arrow = document.createElement('span');
    const chapter = heading.closest('.studio-product-details__chapter');
    const shortExcerpt = chapter ? excerpt(chapter) : '';

    link.href = `#${chapterId(heading, new Set(Array.from(document.querySelectorAll('[id]'), (node) => node.id)))}`;
    number.className = 'studio-product-details-index__number';
    number.textContent = String(indexPosition + 1).padStart(2, '0');
    copy.className = 'studio-product-details-index__copy';
    copy.append(document.createTextNode(normalizeText(heading.textContent)));
    if (shortExcerpt) {
      const description = document.createElement('span');
      description.className = 'studio-product-details-index__excerpt';
      description.textContent = shortExcerpt;
      copy.append(description);
    }
    arrow.className = 'studio-product-details-index__arrow';
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = '→';
    link.append(number, copy, arrow);
    item.append(link);
    list.append(item);
  });

  index.hidden = false;
}

function initialize() {
  const body = document.querySelector(DETAILS_BODY_SELECTOR);
  const indexes = Array.from(document.querySelectorAll(INDEX_SELECTOR));
  if (!body) {
    indexes.forEach(resetIndex);
    return;
  }

  const enhanced = enhanceBody(body);
  indexes.forEach((index) => {
    if (enhanced) buildIndex(body, index);
    else resetIndex(index);
  });
}

function scheduleInitialize() {
  window.requestAnimationFrame(initialize);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scheduleInitialize);
else scheduleInitialize();
document.addEventListener('shopify:section:load', scheduleInitialize);
document.addEventListener('shopify:section:unload', (event) => {
  const target = event.target;
  if (target instanceof Element && target.querySelector(DETAILS_BODY_SELECTOR)) {
    document.querySelectorAll(INDEX_SELECTOR).forEach(resetIndex);
  }
});
